import React, { useEffect, useState, useRef, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share2, Download, Swords, Shield, Star } from 'lucide-react';
import { Button } from './ui/button';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';
import { play as playSound } from '../lib/soundDirector';

const confettiConfigs = {
  rare: {
    particleCount: 100,
    spread: 70,
    origin: { y: 0.6 },
    colors: ['#3B82F6', '#60A5FA', '#93C5FD', '#FFFFFF']
  },
  epic: {
    particleCount: 150,
    spread: 100,
    origin: { y: 0.6 },
    colors: ['#A855F7', '#C084FC', '#D4FF00', '#FFFFFF', '#FFD700']
  },
  legendary: {
    particleCount: 200,
    spread: 120,
    origin: { y: 0.6 },
    colors: ['#FFD700', '#FFA500', '#D4FF00', '#FFFFFF', '#FF6B35'],
    scalar: 1.2
  }
};

const fireConfetti = (rarity) => {
  const config = confettiConfigs[rarity] || confettiConfigs.rare;
  confetti({ ...config, startVelocity: 30 });
  setTimeout(() => confetti({ ...config, startVelocity: 45, decay: 0.92 }), 150);
  if (rarity === 'legendary') {
    setTimeout(() => confetti({ ...config, startVelocity: 55, spread: 150, particleCount: 100 }), 300);
    setTimeout(() => {
      confetti({ spread: 360, ticks: 100, gravity: 0, decay: 0.94, startVelocity: 20, colors: ['#FFD700', '#D4FF00', '#FFFFFF'], particleCount: 50, scalar: 1.5, shapes: ['star'] });
    }, 500);
  }
  if (rarity === 'epic') {
    setTimeout(() => {
      confetti({ ...config, particleCount: 50, angle: 60, spread: 55, origin: { x: 0, y: 0.65 } });
      confetti({ ...config, particleCount: 50, angle: 120, spread: 55, origin: { x: 1, y: 0.65 } });
    }, 200);
  }
};

/** GOLDEN EXPLOSION — Survivant Tier Or */
const fireGoldenExplosion = () => {
  const gold = ['#FFD700', '#FACC15', '#F59E0B', '#FCD34D', '#FFFFFF'];
  const fire = ['#FF6B35', '#EF4444', '#F97316', '#FFD700', '#FBBF24'];

  // Wave 1: Center golden burst
  confetti({ particleCount: 120, spread: 100, origin: { y: 0.5 }, colors: gold, startVelocity: 50, scalar: 1.4, ticks: 120 });

  // Wave 2: Side cannons
  setTimeout(() => {
    confetti({ particleCount: 80, angle: 60, spread: 60, origin: { x: 0, y: 0.7 }, colors: gold, startVelocity: 55, scalar: 1.2 });
    confetti({ particleCount: 80, angle: 120, spread: 60, origin: { x: 1, y: 0.7 }, colors: gold, startVelocity: 55, scalar: 1.2 });
  }, 200);

  // Wave 3: Star shower
  setTimeout(() => {
    confetti({ particleCount: 60, spread: 360, ticks: 150, gravity: 0.3, decay: 0.95, startVelocity: 25, colors: gold, scalar: 2, shapes: ['star'] });
  }, 450);

  // Wave 4: Fire embers
  setTimeout(() => {
    confetti({ particleCount: 40, spread: 180, origin: { y: 0.4 }, colors: fire, startVelocity: 60, gravity: 1.2, scalar: 0.8, ticks: 80 });
  }, 650);

  // Wave 5: Final golden rain
  setTimeout(() => {
    confetti({ particleCount: 150, spread: 200, origin: { y: 0 }, colors: gold, startVelocity: 10, gravity: 0.6, scalar: 1.6, ticks: 200, drift: 0 });
  }, 900);

  // Wave 6: Celebratory stars repeat
  setTimeout(() => {
    confetti({ particleCount: 40, spread: 360, ticks: 120, gravity: 0, decay: 0.96, startVelocity: 15, colors: ['#FFD700', '#FFFFFF'], scalar: 2.5, shapes: ['star'] });
  }, 1200);
};

const rarityColors = {
  common: '#A1A1AA',
  uncommon: '#22C55E',
  rare: '#3B82F6',
  epic: '#A855F7',
  legendary: '#FFD700'
};

const rarityLabels = {
  common: { fr: 'Commun', en: 'Common' },
  uncommon: { fr: 'Peu commun', en: 'Uncommon' },
  rare: { fr: 'Rare', en: 'Rare' },
  epic: { fr: 'Epique', en: 'Epic' },
  legendary: { fr: 'Legendaire', en: 'Legendary' }
};

const TIER_VISUALS = {
  1: { label: 'Acier', gradient: 'from-zinc-400 to-zinc-600', text: 'text-zinc-300', glow: '#A1A1AA' },
  2: { label: 'Bronze', gradient: 'from-amber-600 to-orange-700', text: 'text-amber-400', glow: '#D97706' },
  3: { label: 'Or', gradient: 'from-yellow-400 to-amber-500', text: 'text-yellow-300', glow: '#FACC15' },
};

export const BadgeCelebration = ({ badge, onClose, language = 'fr' }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [sharing, setSharing] = useState(false);
  const shareRef = useRef(null);
  const isSurvivantGold = badge?.survivantTier === 3;
  const survivantTier = badge?.survivantTier || 0;

  useEffect(() => {
    if (!badge) return;
    if (isSurvivantGold) {
      fireGoldenExplosion();
      // Repeat golden rain every 3 seconds for dramatic effect
      const interval = setInterval(() => {
        confetti({ particleCount: 30, spread: 160, origin: { y: 0 }, colors: ['#FFD700', '#FACC15', '#FFFFFF'], startVelocity: 8, gravity: 0.5, scalar: 1.3, ticks: 150 });
      }, 3000);
      return () => clearInterval(interval);
    } else if (['rare', 'epic', 'legendary'].includes(badge.rarity)) {
      fireConfetti(badge.rarity);
    }
    // 🔊 Iter. 390 — synthesized badge unlock chime (rising major arpeggio)
    try { playSound('badge_unlocked'); } catch (_) { /* sound non-critical */ }
  }, [badge, isSurvivantGold]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handleShare = useCallback(async () => {
    if (!shareRef.current) return;
    setSharing(true);
    try {
      const dataUrl = await toPng(shareRef.current, { pixelRatio: 3, cacheBust: true, backgroundColor: '#0a0a0f' });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], 'survivant-gold-yumix.png', { type: 'image/png' });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'YUMIX SURVIVANT',
          text: language === 'fr'
            ? 'Badge SURVIVANT Or obtenu! Le terrain ne m\'a pas brise.'
            : 'Gold SURVIVOR badge earned! The terrain did not break me.',
        });
      } else {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = 'survivant-gold-yumix.png';
        link.click();
        toast.success(language === 'fr' ? 'Image telechargee!' : 'Image downloaded!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error(language === 'fr' ? 'Erreur de partage' : 'Share error');
      }
    } finally {
      setSharing(false);
    }
  }, [language]);

  if (!badge) return null;

  const rarityColor = isSurvivantGold ? '#FACC15' : (rarityColors[badge.rarity] || rarityColors.common);
  const rarityLabel = rarityLabels[badge.rarity]?.[language] || badge.rarity;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: isSurvivantGold ? 'rgba(0, 0, 0, 0.92)' : 'rgba(0, 0, 0, 0.85)' }}
          onClick={handleClose}
          data-testid="badge-celebration-overlay"
        >
          {/* Coach Yuri */}
          <motion.img
            src="/mascot-yuri.png"
            alt="Coach Yuri"
            initial={{ opacity: 0, x: 100, y: 100 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ delay: 0.3, type: 'spring' }}
            className="absolute bottom-4 right-4 w-32 md:w-48 h-auto object-contain drop-shadow-2xl hidden md:block"
          />

          {/* === SURVIVANT GOLD CELEBRATION === */}
          {isSurvivantGold ? (
            <motion.div
              initial={{ scale: 0.3, opacity: 0, rotateY: 180 }}
              animate={{ scale: 1, opacity: 1, rotateY: 0 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', damping: 12, stiffness: 200, duration: 0.8 }}
              className="relative max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Shareable card (captured by html-to-image) */}
              <div
                ref={shareRef}
                className="rounded-2xl p-8 text-center relative overflow-hidden"
                style={{
                  background: 'linear-gradient(145deg, #1a1500 0%, #0a0a0f 40%, #1a1200 100%)',
                  border: '3px solid #FACC15',
                  boxShadow: '0 0 80px rgba(250,204,21,0.35), 0 0 160px rgba(250,204,21,0.15), inset 0 0 60px rgba(250,204,21,0.05)',
                }}
                data-testid="survivant-gold-celebration-card"
              >
                {/* Animated golden ring */}
                <motion.div
                  className="absolute inset-0 rounded-2xl"
                  style={{ border: '2px solid transparent', borderImage: 'linear-gradient(135deg, #FFD700, transparent, #FACC15, transparent, #FFD700) 1' }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                />

                {/* Close */}
                <button onClick={handleClose} className="absolute top-4 right-4 text-yellow-500/60 hover:text-yellow-300 transition-colors z-20">
                  <X className="w-6 h-6" />
                </button>

                {/* Golden shield icon */}
                <motion.div
                  initial={{ scale: 0, rotate: -30 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.3, type: 'spring', damping: 8 }}
                  className="mb-6 relative inline-block"
                >
                  <motion.div
                    className="w-24 h-24 mx-auto rounded-full flex items-center justify-center relative"
                    style={{ background: 'radial-gradient(circle, rgba(250,204,21,0.3) 0%, transparent 70%)' }}
                    animate={{ boxShadow: ['0 0 30px rgba(250,204,21,0.4)', '0 0 60px rgba(250,204,21,0.6)', '0 0 30px rgba(250,204,21,0.4)'] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <Shield className="w-12 h-12 text-yellow-400 absolute opacity-40" />
                    <Swords className="w-12 h-12 text-yellow-300 relative z-10" />
                  </motion.div>
                  {/* Gold star */}
                  <motion.div
                    className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <Star className="w-4 h-4 text-black fill-black" />
                  </motion.div>
                </motion.div>

                {/* Title */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                  <p className="font-heading text-xs uppercase tracking-[0.3em] text-yellow-500/70 mb-2">
                    {language === 'fr' ? 'Promotion Legendaire' : 'Legendary Promotion'}
                  </p>
                  <motion.h2
                    className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-amber-400 to-yellow-300 mb-2"
                    animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                    transition={{ duration: 3, repeat: Infinity }}
                    style={{ backgroundSize: '200% 100%' }}
                    data-testid="survivant-gold-title"
                  >
                    SURVIVANT x{badge.survivantCount || 3}
                  </motion.h2>
                  <p className="text-yellow-400/80 font-heading text-sm uppercase tracking-wider">
                    {language === 'fr' ? 'Niveau Or' : 'Gold Tier'}
                  </p>
                </motion.div>

                {/* Tier progression */}
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-6 mb-4">
                  <div className="flex items-center gap-1.5 justify-center mb-2">
                    {[1, 2, 3].map(t => (
                      <motion.div
                        key={t}
                        className={`h-2 w-16 rounded-full bg-gradient-to-r ${TIER_VISUALS[t].gradient}`}
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ delay: 0.7 + t * 0.2, duration: 0.4 }}
                        style={{ transformOrigin: 'left' }}
                      />
                    ))}
                  </div>
                  <div className="flex justify-center gap-8 text-[10px] uppercase tracking-wider">
                    <span className="text-zinc-400">Acier</span>
                    <span className="text-amber-500">Bronze</span>
                    <span className="text-yellow-300 font-bold">Or</span>
                  </div>
                </motion.div>

                {/* Quote */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 }}
                  className="mt-4 p-3 rounded-xl border border-yellow-500/20 bg-yellow-500/5"
                >
                  <p className="text-sm text-yellow-200/80 italic leading-relaxed">
                    {language === 'fr'
                      ? '"Le parcours t\'a frappe 3 fois. 3 fois tu t\'es releve. Tu n\'es plus un debutant. Tu es une legende."'
                      : '"The terrain hit you 3 times. 3 times you got back up. You\'re no longer a soldier. You\'re a legend."'}
                  </p>
                  <p className="text-[10px] text-yellow-500/50 mt-1 text-right">— YUMIX</p>
                </motion.div>

                {/* YUMIX branding for share */}
                <div className="mt-4 flex items-center justify-center gap-2 text-yellow-500/40 text-[10px] uppercase tracking-wider">
                  <span>YUMIX</span>
                  <span>Extra Miles</span>
                </div>
              </div>

              {/* Action buttons (outside shareable area) */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
                className="mt-4 flex gap-3 justify-center"
              >
                <Button
                  onClick={handleShare}
                  disabled={sharing}
                  className="px-6 py-3 font-heading uppercase tracking-wider bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black font-bold"
                  data-testid="survivant-gold-share-btn"
                >
                  {sharing ? (
                    <span className="animate-spin mr-2">...</span>
                  ) : (
                    <Share2 className="w-4 h-4 mr-2" />
                  )}
                  {language === 'fr' ? 'Partager' : 'Share'}
                </Button>
                <Button
                  onClick={handleClose}
                  variant="outline"
                  className="px-6 py-3 font-heading uppercase tracking-wider border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                  data-testid="survivant-gold-close-btn"
                >
                  {language === 'fr' ? 'Fermer' : 'Close'}
                </Button>
              </motion.div>
            </motion.div>
          ) : (
            /* === STANDARD BADGE CELEBRATION === */
            <motion.div
              initial={{ scale: 0.5, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{ type: 'spring', damping: 15, stiffness: 300 }}
              className="relative max-w-md w-full rounded-2xl p-8 text-center"
              style={{
                background: `linear-gradient(135deg, ${rarityColor}20 0%, #18181B 50%, ${rarityColor}10 100%)`,
                border: `3px solid ${rarityColor}`,
                boxShadow: `0 0 60px ${rarityColor}40, 0 0 120px ${rarityColor}20`
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={handleClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors">
                <X className="w-6 h-6" />
              </button>

              {/* Survivant tier upgrade (non-gold) */}
              {survivantTier > 0 && survivantTier < 3 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="mb-4">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    {[1, 2, 3].map(t => (
                      <div key={t} className={`h-1.5 w-12 rounded-full ${t <= survivantTier ? `bg-gradient-to-r ${TIER_VISUALS[t].gradient}` : 'bg-muted/30'}`} />
                    ))}
                  </div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {language === 'fr' ? `Niveau ${TIER_VISUALS[survivantTier]?.label}` : `${TIER_VISUALS[survivantTier]?.label} Tier`}
                  </p>
                </motion.div>
              )}

              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: 'spring', damping: 10 }} className="mb-6">
                {badge.badge_image ? (
                  <img src={badge.badge_image} alt={badge.name || badge.icon} className="w-20 h-20 mx-auto mb-4 object-contain drop-shadow-[0_0_15px_rgba(239,68,68,0.5)]" />
                ) : (
                  <span className="text-6xl block mb-4">{badge.icon}</span>
                )}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                  <h2 className="font-heading text-3xl font-black uppercase tracking-tight mb-2" style={{ color: rarityColor }}>
                    {language === 'fr' ? 'Nouveau Badge!' : 'New Badge!'}
                  </h2>
                </motion.div>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                <h3 className="font-heading text-2xl font-bold text-foreground mb-2">{badge.name}</h3>
                <p className="text-muted-foreground mb-4">{badge.description}</p>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider"
                  style={{ backgroundColor: `${rarityColor}20`, color: rarityColor, border: `1px solid ${rarityColor}50` }}>
                  {badge.rarity === 'legendary' && <Star className="w-3.5 h-3.5" />}
                  {rarityLabel}
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-8">
                <Button onClick={handleClose} className="px-8 py-3 font-heading uppercase tracking-wider"
                  style={{ backgroundColor: rarityColor, color: badge.rarity === 'legendary' || badge.rarity === 'epic' ? '#000' : '#FFF' }}>
                  {language === 'fr' ? 'Genial!' : 'Awesome!'}
                </Button>
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BadgeCelebration;
